import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ChevronLeft as ChevronLeftIcon } from 'lucide-react';
import { useSchedule } from '../../hooks/useSchedule';
import { Card, Button } from '../../components/common';
import DatePicker from '../../components/common/DatePicker';
import { toast } from 'react-hot-toast';

const EditSchedulePage: React.FC = () => {
  const { scheduleId, teamId } = useParams<{ scheduleId: string; teamId: string }>();
  const navigate = useNavigate();
  const { schedule, isLoading: isLoadingSchedule, error: scheduleError } = useSchedule(scheduleId);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Form state
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  // Initialize form values when schedule loads
  useEffect(() => {
    if (schedule) {
      try {
        setStartDate(parseISO(schedule.start_date));
        setEndDate(parseISO(schedule.end_date));
      } catch (parseError) {
        console.error('Error parsing dates:', parseError);
      }
    }
  }, [schedule]);
  
  if (isLoadingSchedule || !scheduleId) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!schedule || scheduleError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading schedule. Please try again.
      </div>
    );
  }
  
  const handleSave = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select valid dates');
      return;
    }
    
    setIsUpdating(true);
    
    try {
      // Make API call to update schedule
      const response = await fetch(`/api/schedules/${scheduleId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update schedule');
      }
      
      toast.success('Schedule updated successfully');
      navigate(`/teams/${teamId}/schedules/${scheduleId}`);
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('Failed to update schedule. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMMM d, yyyy');
    } catch (formatError) {
      console.error('Error formatting date:', formatError);
      return dateString;
    }
  };
  
  const scheduleTitle = `${formatDate(schedule.start_date)} - ${formatDate(schedule.end_date)}`;
  const isEditable = schedule.status === 'draft';
  
  return (
    <>
      <div className="mb-6">
        <Link
          to={`/teams/${teamId}/schedules/${scheduleId}`}
          className="inline-flex items-center text-primary hover:text-primary-dark"
        >
          <ChevronLeftIcon size={16} className="mr-1" />
          Back to Schedule
        </Link>
      </div>
      
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Schedule: {scheduleTitle}</h1>
      </div>
      
      <div className="py-6">
        <Card>
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Schedule Details</h3>
            <p className="mt-1 text-sm text-gray-500">
              Update the schedule period dates.
            </p>
          </div>
          
          <div className="px-4 py-5 sm:p-6">
            {!isEditable && (
              <div className="mb-6 bg-yellow-50 p-4 rounded-md border border-yellow-200">
                <p className="text-yellow-700">
                  This schedule can no longer be edited because its status is <strong>{schedule.status}</strong>.
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <DatePicker
                  label="Start Date"
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  disabled={!isEditable || isUpdating}
                />
              </div>
              
              <div>
                <DatePicker
                  label="End Date"
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  minDate={startDate || undefined}
                  disabled={!isEditable || isUpdating}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-4">
              <Button
                variant="secondary"
                onClick={() => navigate(`/teams/${teamId}/schedules/${scheduleId}`)}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              
              <Button
                variant="primary"
                onClick={handleSave}
                isLoading={isUpdating}
                disabled={!isEditable || !startDate || !endDate}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};

export default EditSchedulePage; 